import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const KabbalahNumerologyCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Kabbalah Letter Values
  const kabbalahValues = {
    'A': 1, 'J': 1, 'S': 1,
    'B': 2, 'K': 2, 'T': 2,
    'C': 3, 'L': 3, 'U': 3,
    'D': 4, 'M': 4, 'V': 4,
    'E': 5, 'N': 5, 'W': 5,
    'F': 8, 'O': 6, 'X': 6,
    'G': 3, 'P': 8, 'Y': 7,
    'H': 5, 'Q': 1, 'Z': 8,
    'I': 1, 'R': 2
  };

  // Kabbalah Number Meanings
  const numberMeanings = {
    1: {
      title: 'Individuality and Leadership',
      description: 'You are a natural leader with strong personal drive and independence. Your creativity and originality make you stand out.'
    },
    2: {
      title: 'Balance and Harmony',
      description: 'You excel in creating balance and harmony in all aspects of life. Your diplomatic nature helps you build strong relationships.'
    },
    3: {
      title: 'Expression and Creativity',
      description: 'You are a creative soul with excellent communication skills. Your optimistic nature brings joy to those around you.'
    },
    4: {
      title: 'Stability and Practicality',
      description: 'You are grounded and practical, with a strong sense of responsibility. Your attention to detail makes you reliable.'
    },
    5: {
      title: 'Freedom and Adventure',
      description: 'You thrive on change and new experiences. Your adaptability and versatility help you navigate life\'s challenges.'
    },
    6: {
      title: 'Love and Harmony',
      description: 'You are nurturing and caring, with a strong sense of responsibility towards others. Your harmonious nature brings peace.'
    },
    7: {
      title: 'Wisdom and Intuition',
      description: 'You are deeply intuitive and analytical. Your quest for knowledge and understanding guides your life journey.'
    },
    8: {
      title: 'Power and Success',
      description: 'You have a natural ability to achieve success and material abundance. Your leadership qualities inspire others.'
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateKabbalahNumerology = () => {
    if (!name) {
      setError('Please enter your name');
      return;
    }

    // Calculate Kabbalah Number
    const nameNumbers = name
      .toUpperCase()
      .split('')
      .filter(char => char.match(/[A-Z]/))
      .map(char => kabbalahValues[char] || 0)
      .reduce((acc, num) => acc + num, 0);

    let kabbalahNumber = nameNumbers;
    while (kabbalahNumber > 9) {
      kabbalahNumber = kabbalahNumber.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
    }

    // Calculate letter values distribution
    const letterValues = {};
    name
      .toUpperCase()
      .split('')
      .filter(char => char.match(/[A-Z]/))
      .forEach(char => {
        const value = kabbalahValues[char];
        if (value) {
          letterValues[value] = (letterValues[value] || 0) + 1;
        }
      });

    // Prepare chart data
    const kabbalahChartData = {
      labels: Object.keys(letterValues).map(Number).sort().map(String),
      datasets: [{
        label: 'Letter Values Distribution',
        data: Object.keys(letterValues).sort().map(key => letterValues[key]),
        backgroundColor: [
          'rgba(79, 70, 229, 0.5)',
          'rgba(147, 51, 234, 0.5)',
          'rgba(99, 102, 241, 0.5)',
          'rgba(168, 85, 247, 0.5)',
          'rgba(139, 92, 246, 0.5)',
          'rgba(129, 140, 248, 0.5)',
          'rgba(190, 180, 250, 0.5)',
          'rgba(167, 139, 250, 0.5)'
        ],
        borderColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(147, 51, 234, 1)',
          'rgba(99, 102, 241, 1)',
          'rgba(168, 85, 247, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(129, 140, 248, 1)',
          'rgba(190, 180, 250, 1)',
          'rgba(167, 139, 250, 1)'
        ],
        borderWidth: 1
      }]
    };

    setResults({
      name,
      kabbalahNumber,
      numberMeaning: numberMeanings[kabbalahNumber]
    });

    setChartData(kabbalahChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Kabbalah Numerology Calculator | Calculate Your Kabbalah Number</title>
        <meta name="description" content="Calculate your Kabbalah numerology number using our advanced calculator. Understand your spiritual path and life purpose through Kabbalistic wisdom." />
        <meta name="keywords" content="kabbalah numerology, kabbalah calculator, spiritual numerology, kabbalistic wisdom, name analysis" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/kabbalah-numerology" />
        <meta property="og:title" content="Kabbalah Numerology Calculator | Calculate Your Kabbalah Number" />
        <meta property="og:description" content="Calculate your Kabbalah numerology number using our advanced calculator. Understand your spiritual path and life purpose through Kabbalistic wisdom." />
        <meta property="og:image" content="https://yourwebsite.com/images/kabbalah-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/kabbalah-numerology" />
        <meta property="twitter:title" content="Kabbalah Numerology Calculator | Calculate Your Kabbalah Number" />
        <meta property="twitter:description" content="Calculate your Kabbalah numerology number using our advanced calculator. Understand your spiritual path and life purpose through Kabbalistic wisdom." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/kabbalah-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">✨</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Kabbalah Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Kabbalah Number</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Your Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={handleNameChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your full name"
          />
        </div>

        <button
          onClick={calculateKabbalahNumerology}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white mt-6"
        >
          Calculate Kabbalah Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Kabbalah Profile</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.name}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Kabbalah Number:</h5>
                    <p className="text-purple-600 text-4xl text-center">{results.kabbalahNumber}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Your Number's Meaning:</h5>
                    <p className="text-purple-600 text-xl mb-4">{results.numberMeaning.title}</p>
                    <p className="text-gray-700">{results.numberMeaning.description}</p>
                  </div>
                </div>
              </div>

              {/* Letter Values Distribution */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Letter Values Distribution</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Distribution of Letter Values',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        },
                        x: {
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Number Meanings <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.entries(numberMeanings).map(([number, meaning]) => (
              <div key={number} className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-purple-600 mb-4">Number {number}</h3>
                <p className="text-gray-700">{meaning.title}</p>
                <p className="text-gray-600 mt-2">{meaning.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Kabbalah numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Name</h3>
              <p className="text-gray-700">
                Enter your full name in the input field. The calculator will analyze each letter according to Kabbalah numerology values.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Kabbalah Number" button to see your results. The calculator will show your Kabbalah number and its meaning.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Kabbalah number and its meaning to understand your spiritual path and life purpose according to Kabbalistic wisdom.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Kabbalah Numerology?</h3>
              <p className="text-gray-700">
                Kabbalah Numerology is an ancient system that assigns specific numerical values to letters of the alphabet. It reveals insights about your personality, life purpose, and spiritual path.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it different from other numerology systems?</h3>
              <p className="text-gray-700">
                Kabbalah Numerology uses a unique system of letter values that differs from Pythagorean or Chaldean systems. It focuses on spiritual and metaphysical aspects of your life.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does my Kabbalah number mean?</h3>
              <p className="text-gray-700">
                Each Kabbalah number has specific meanings and interpretations. The visualization graph helps you understand the distribution of letter values in your name.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Kabbalah Number calculation</li>
                <li>Letter values distribution</li>
                <li>Number meanings interpretation</li>
                <li>Visual analysis</li>
                <li>Comprehensive explanations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand spiritual path</li>
                <li>Discover life purpose</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve understanding of self</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KabbalahNumerologyCalculator;
